<script lang="ts">
	import CaseStudyCard from "@components/CaseStudyCard.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import type { ClassValue } from "svelte/elements";
	import arrowIcon from "@core/assets/icons/arrow.svg?raw";
	import { ButtonVariant } from "@core/schema/ButtonVariant.js";
	import type { PublicCaseStudy } from "@core/schema/PublicCaseStudy.js";
	import { getCaseStudiesAnchor } from "@core/schema/paths/getCaseStudiesAnchor.js";
	import { getCaseStudiesPath } from "@core/schema/paths/getCaseStudiesPath.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import HugeHeading from "../HugeHeading.svelte";
	import CaseStudyCarousel from "./CaseStudyCarousel.svelte";

	const {
		caseStudies,
		mainCaseStudy,
		heading,
		class: customClass,
	}: {
		caseStudies: PublicCaseStudy[];
		mainCaseStudy?: PublicCaseStudy;
		heading: string;
		class?: ClassValue;
	} = $props();
	const { tenant } = getGlobalTemplateProps();
	const allCaseStudies = mainCaseStudy ? [...caseStudies, mainCaseStudy] : caseStudies;
	let activeIndex = $state(allCaseStudies.length - 1);
	let canPrev = $state(false);
	let canNext = $state(true);
	let carousel: { next(): void; prev(): void } | undefined;
</script>

<!--
	[&:is(*)] is used to overwrite full-width's styles by increasing specificity.
 	!important isn't suitable, as we accept `customClass` and thus padding should be overridable
-->
<section
	class={[customClass, "full-width overflow-x-hidden py-8 md:py-16 lg:py-20 flex flex-col"]}
	id={getCaseStudiesAnchor(tenant)}
>
	<div
		class="flex flex-col justify-center items-center gap-6 xl:gap-12 overflow-x-visible [&:is(&)]:px-[calc(var(--container-gap)+1.5rem)] max-w-[min(120rem,99dvw)] w-screen self-center"
	>
		<div class="self-stretch flex gap-8 lg:gap-16 flex-col xl:flex-row xl:justify-between w-full xl:items-center">
			<HugeHeading level={2} text={heading} class="xl:min-h-[1.8em]" />
			<div class="flex flex-col lg:gap-16 md:gap-12 gap-4 max-w-[37.25rem]">
				<div class="md:text-[1.25rem]/[1.4] text-xs/[1.4] font-semibold">
					<Formatted
						text="Miliony ušetřených korun, fungující sklady i logistické týmy nebo méně papírování a rychlejší procesy. Podívejte se v případových studiích, kdo má díky Commarec logistiku pod kontrolou."
					/>
				</div>
				<Link
					href={getCaseStudiesPath(tenant)}
					variant={ButtonVariant.Outlined}
					icon="arrow"
					class="xl:inline-flex hidden gap-8 w-fit"
				>
					<Formatted text="Více případových studií" />
				</Link>
			</div>
		</div>
		<div class="flex flex-col xl:flex-row gap-2 xl:gap-[30%] w-full">
			<div class="flex lg:flex-col justify-end gap-4 xl:ml-8 px-2 xl:px-0">
				<div class="flex gap-1 xl:gap-2 md:w-auto w-full">
					<button
						type="button"
						class="h-6 px-2 xl:w-16 xl:h-10 xl:p-2 flex flex-grow lg:flex-grow-0 items-center justify-center rounded-lg xl:rounded-xl border xl:border-2 border-cmcnavy text-cmcnavy disabled:opacity-30 transition-colors hover:bg-cmcnavy hover:text-white"
						disabled={!canPrev}
						onclick={(): void => {
							carousel?.prev();
						}}
						aria-label="Zpět"
					>
						<Icon icon={arrowIcon} class="size-5 xl:size-8 rotate-180" />
					</button>
					<button
						type="button"
						class="h-6 px-2 xl:w-16 xl:h-10 xl:p-2 flex flex-grow lg:flex-grow-0 items-center justify-center rounded-lg xl:rounded-xl border xl:border-2 border-cmcnavy text-cmcnavy disabled:opacity-30 transition-colors hover:bg-cmcnavy hover:text-white"
						disabled={!canNext}
						onclick={(): void => {
							carousel?.next();
						}}
						aria-label="Další"
					>
						<Icon icon={arrowIcon} class="size-5 xl:size-8" />
					</button>
				</div>
			</div>

			<div class="grow relative overflow-visible xl:-mt-12">
				<CaseStudyCarousel bind:this={carousel} bind:activeIndex items={allCaseStudies} bind:canNext bind:canPrev>
					{#snippet item(caseStudy)}
						<div class="pr-1 xl:pr-2 h-full">
							<CaseStudyCard {caseStudy} class="size-full bg-white shadow-sm border overflow-hidden border-gray-100" />
						</div>
					{/snippet}
				</CaseStudyCarousel>
			</div>
			<div class="flex w-full xl:hidden justify-center mt-4">
				<Link href={getCaseStudiesPath(tenant)} variant={ButtonVariant.Outlined} icon="arrow">
					<Formatted text="Více případových studií" />
				</Link>
			</div>
		</div>
	</div>
</section>
